import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, MapPin, Layers, Calendar, UserPlus, Hexagon, Check, X,
  Plus, Trash2, ChevronLeft, ChevronRight, Loader2, Home
} from 'lucide-react';
import { Button } from '../components/ui/button';
import AddressLookup from '../components/AddressLookup';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { onboardingApi, holidaysApi, locationsApi, departmentsApi, usersApi, settingsApi, leaveApi } from '../lib/api';
// ⚠️ Pure, and CHECKED — `node src/lib/companyHolidays.checks.mjs`. Signup and Settings
// must count "our bank holidays" the same way; two copies is how they drift.
import { companyBankHolidays, otherCountryCodes } from '../lib/companyHolidays';
import { formatDateGB } from '../lib/dates';
import { holidayScopeLabel } from '../lib/regions';
// ⚠️ The statutory line under the total — WORDS only. The law and the floor for the
// company's week come from GET /settings/jurisdiction (Group A4, 21/10/2026).
import { statutoryLine } from '../lib/statutoryLine';
// ⚠️ THE SAME LIST AND THE SAME WORDS THE HOLIDAYS PAGE USES — Rob's option A,
// 13/08/2026: *"otherwise we are possibly leaving a trail of jobs for another day."*
import HolidayDayList from '../components/HolidayDayList';
import { removeQuestion, removedMessage, restoredMessage } from '../lib/holidayActions';
import { placesIn } from '../lib/holidayPlaces';
import AddCompanyDayForm from '../components/AddCompanyDayForm';
// ⚠️ ONE county control for the whole app — there were three lists and they had drifted
// (see src/lib/counties.js). Onboarding was a FOURTH, and the worst of them: a plain
// typing box, so the very first county a customer ever enters was the one most likely to
// be spelled differently from everywhere else.
import CountyField from '../components/CountyField';
// ⚠️ Pure and CHECKED — `node src/lib/workingWeek.checks.mjs`. `buildWeek` writes every
// day out explicitly, because the backend turns an EMPTY day list into Monday-to-Friday
// silently, and a wrong answer nobody typed is the fault this whole step exists to avoid.
import { DAY_KEYS, MONDAY_TO_FRIDAY, buildWeek, daysWorkedIn, describeDays } from '../lib/workingWeek';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';

const STEP_COLORS = {
  blue:   { chip: 'bg-blue-500/15 text-blue-600',     tab: 'bg-blue-500/10 text-blue-600 border-blue-500/40',       bar: 'bg-blue-500',    btn: 'bg-blue-600 hover:bg-blue-700' },
  purple: { chip: 'bg-purple-500/15 text-purple-600', tab: 'bg-purple-500/10 text-purple-600 border-purple-500/40', bar: 'bg-purple-500',  btn: 'bg-purple-600 hover:bg-purple-700' },
  amber:  { chip: 'bg-amber-500/15 text-amber-600',   tab: 'bg-amber-500/10 text-amber-600 border-amber-500/40',    bar: 'bg-amber-500',   btn: 'bg-amber-600 hover:bg-amber-700' },
  green:  { chip: 'bg-emerald-500/15 text-emerald-600', tab: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/40', bar: 'bg-emerald-500', btn: 'bg-emerald-600 hover:bg-emerald-700' },
  red:    { chip: 'bg-rose-500/15 text-rose-600',     tab: 'bg-rose-500/10 text-rose-600 border-rose-500/40',       bar: 'bg-rose-500',    btn: 'bg-rose-600 hover:bg-rose-700' },
};

const DEPT_SUGGESTIONS = ['Sales', 'Marketing', 'Operations', 'Finance', 'HR', 'IT', 'Engineering', 'Customer Support'];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({ industries: [], referral_sources: [], counties: [], public_holiday_regions: [] });
  const [loadedHolidayCount, setLoadedHolidayCount] = useState(0);
  const [loadedHolidays, setLoadedHolidays] = useState([]);
  const [optedOutHolidays, setOptedOutHolidays] = useState([]);
  const [otherPlaceCount, setOtherPlaceCount] = useState(0);
  // Every place this company has holidays for — what the add-a-day picker offers.
  const [holidayPlaces, setHolidayPlaces] = useState([]);
  const [showHolidayDates, setShowHolidayDates] = useState(false);
  // ⚠️ THE TICK IS ITS OWN ENDPOINT, NOT A FIELD ON THE LEAVE-POLICY SAVE.
  // I added a second writer of `holidays_confirmed_year` on 13/08/2026 —
  // `POST /holidays/review/confirm` already existed and is what the Holidays page calls.
  // Two ways to record one fact is the exact thing this week has been spent deleting.
  const [holidaysConfirmed, setHolidaysConfirmed] = useState(false);
  const [existingLocations, setExistingLocations] = useState([]);

  // Countries and their regions come from the SAME endpoint the location form uses, so
  // signup and Settings can never offer different lists. Scotland disappeared from one
  // hand-maintained copy on 01/08/2026; that is why this is fetched, not typed.
  const [countries, setCountries] = useState([]);
  const [regionsByCountry, setRegionsByCountry] = useState({});
  const regionsFor = (code) => (code && regionsByCountry[code]) || [];
  // ⚠️ FALL BACK TO THE CODE, NEVER TO A BLANK — same rule the Settings and Locations
  // pages use. "Algeria" is what an admin wants, "DZ" is still usable, and an empty
  // string would read as a rendering fault rather than missing data.
  const countryName = (code) =>
    countries.find((c) => c.code === code)?.name || code;
  // The same code -> name map the Holidays page passes around, built from the countries
  // this page has already fetched. Without it a place picker would offer "DZ".
  const countryNamesByCode = () =>
    Object.fromEntries(countries.map((c) => [c.code, c.name]));

  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    address_line_1: '', address_line_2: '', address_line_3: '', city: '', county: '', postcode: '',
    company_phone: '', company_website: '', industry: '', employee_count: '',
    vat_number: '', company_reg_number: '',
    country_code: '', public_holiday_region: '',
  });
  const [adminInfo, setAdminInfo] = useState({ job_title: '', mobile_phone: '', referral_source: '' });
  const [leavePolicy, setLeavePolicy] = useState({
    leave_year_start_month: 1, default_annual_allowance: 28, 
    public_holiday_region: '', bank_holidays_included: true,
    // ⚠️ null = "not loaded and not typed", which is NOT the same as 0. It is sent as null,
    // and the server leaves the admin's "days already taken" exactly as it is. Sending 0
    // here used to be harmless-looking and was the opposite: the server REPLACED the
    // admin's whole "used" figure with it, wiping every booking on their own balance
    // (BUGS.md, 24/09/2026). Filled in below from what is already recorded.
    admin_opening_balance_used: null,
  });
  // The working week, asked ONLY where the country says it is worth asking (Rob's option
  // A, 13/08/2026). `weekSuggestion` is what the country usually does; `weekChoice` is
  // what the admin said. ⚠️ `weekChoice` STARTS NULL AND NOTHING IS PRE-SELECTED — the
  // whole reason `suggestion_for` refuses to auto-apply is that an answer nobody typed is
  // invisible, which is how an Edinburgh office ended up on English bank holidays.
  const [weekSuggestion, setWeekSuggestion] = useState(null);
  // The company's statutory facts and its floor (GET /settings/jurisdiction), or null.
  const [jurisdiction, setJurisdiction] = useState(null);
  const [weekChoice, setWeekChoice] = useState(null);
  // The week already saved, if this company has been set up before. `[]` means none.
  const [savedWeek, setSavedWeek] = useState([]);
  const [customDays, setCustomDays] = useState(MONDAY_TO_FRIDAY);
  const [locations, setLocations] = useState([]);

  // ⚠️ THESE MUST SIT BELOW THE `useState` CALLS THEY READ. `const` is hoisted but not
  // initialised, so reading `weekSuggestion` from a const declared above it throws
  // "Cannot access 'weekSuggestion' before initialization" and the whole page renders as
  // a red error screen. Caught 13/08/2026 by opening the page — `scope.checks.mjs` proves
  // every name RESOLVES, which is not the same as it being usable at that point in the
  // file. LocationsPage carries the identical warning above its own derived values.
  // Is this country's usual week something other than Monday to Friday? Only then is the
  // question worth putting in front of a customer at signup.
  //
  // ⚠️ A COUNTRY WE HOLD NOTHING FOR IS NOT ASKED. `suggestion_for` returns null both for
  // the ~230 countries nobody has looked at and for the five we deliberately refuse to
  // guess about — in every one of those cases the honest move is to say nothing and leave
  // the company on the app default, not to invent a question with no informed answer.
  // ⚠️ WHICH RADIO A SAVED WEEK CORRESPONDS TO. Compared as a SET of day names, because
  // `buildWeek` writes every day out explicitly and the stored JSON also carries times —
  // two weeks that work the same days are the same answer even if the hours differ.
  const matches = (a, b) => a.length === b.length && a.every((d) => b.includes(d));
  const savedChoice = () => {
    if (!savedWeek.length) return null;
    if (weekSuggestion && matches(savedWeek, weekSuggestion.days)) return 'suggested';
    if (matches(savedWeek, MONDAY_TO_FRIDAY)) return 'monday-to-friday';
    return 'custom';
  };

  const suggestedWeekDiffers = !!weekSuggestion
    && String(weekSuggestion.days) !== String(MONDAY_TO_FRIDAY);

  // The week to SAVE, or null when the admin was never asked / never answered.
  //
  // ⚠️ NULL MEANS "DON'T WRITE ANYTHING". The backend leaves `business_hours` alone for
  // null, so an unanswered question changes nothing — it must never resolve to a
  // Monday-to-Friday that looks like a decision.
  const chosenWeek = () => {
    if (!suggestedWeekDiffers || !weekChoice) return null;
    if (weekChoice === 'suggested') return buildWeek(weekSuggestion.days);
    if (weekChoice === 'monday-to-friday') return buildWeek(MONDAY_TO_FRIDAY);
    return customDays.length ? buildWeek(customDays) : null;
  };
  const [departments, setDepartments] = useState([]);
  const [invites, setInvites] = useState([]);

  const steps = [
    { key: 'company', label: 'Company', Icon: Building2, color: 'blue' },
    { key: 'locations', label: 'Locations', Icon: MapPin, color: 'purple' },
    { key: 'departments', label: 'Departments', Icon: Layers, color: 'amber' },
    { key: 'leave', label: 'Leave policy', Icon: Calendar, color: 'green' },
    { key: 'team', label: 'Invite team', Icon: UserPlus, color: 'red' },
  ];
  const active = steps[step];
  const ac = STEP_COLORS[active.color];
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  useEffect(() => {
    onboardingApi.getOptions().then(r => setOptions(r.data)).catch(() => {});
    holidaysApi.getCountries()
      .then(r => {
        setCountries(r.data.countries || []);
        setRegionsByCountry(r.data.regions_by_country || {});
      })
      .catch(() => {});
    // ⚠️ O9, 13/08/2026 — THE WIZARD SHOWS WHAT IS ALREADY THERE.
    //
    // It used to restore the company NAME and nothing else, so re-opening a finished
    // setup presented an empty form over a real company. Clicking a step chip then posted
    // those blanks: `handleNext` refuses to move without a country, but `goToStep` never
    // asked, and `tenant.country_code` became `""` — taking their bank holidays with it.
    //
    // ⚠️ IT READS `/settings`, NOT A NEW ONBOARDING FIELD. That endpoint already returns
    // exactly these fields and is what the Settings screen reads, so the wizard and
    // Settings cannot disagree about what the company currently is. Adding the same
    // fields to `/onboarding/status` would have been a second description of one thing —
    // which is the fault this whole week has been spent removing.
    //
    // ⚠️ AND THE BACKEND NO LONGER TRUSTS THIS TO HAVE WORKED. `routers/onboarding.given`
    // ignores blanks regardless, because a failed load here must not be able to delete
    // anything. Two layers: this one makes the screen honest, that one makes it safe.
    settingsApi.get()
      .then((r) => {
        const t = r.data || {};
        setCompanyInfo((p) => ({
          ...p,
          // `?? p.x` not `|| p.x` — an empty string from the server is a real answer
          // ("this is not filled in"), and the form should show it as empty.
          company_name: t.company_name ?? p.company_name,
          address_line_1: t.address_line_1 ?? p.address_line_1,
          address_line_2: t.address_line_2 ?? p.address_line_2,
          address_line_3: t.address_line_3 ?? p.address_line_3,
          city: t.city ?? p.city,
          county: t.county ?? p.county,
          postcode: t.postcode ?? p.postcode,
          company_phone: t.company_phone ?? p.company_phone,
          company_website: t.company_website ?? p.company_website,
          industry: t.industry ?? p.industry,
          employee_count: t.employee_count ?? p.employee_count,
          country_code: t.country_code ?? p.country_code,
          public_holiday_region: t.public_holiday_region ?? p.public_holiday_region,
        }));
        // ⚠️ AND THE WORKING WEEK THEY ALREADY CHOSE. Leaving all three radios blank over
        // a saved Sunday-to-Thursday week is the same fault as the empty address, one
        // field along: the screen would be showing nothing where there is something.
        // Harmless today (an unanswered question writes nothing) and misleading, which is
        // exactly how the wipe started — a form that does not show what it is sitting on.
        setSavedWeek(daysWorkedIn(t.business_hours));
        // ⚠️ AND THE LEAVE STEP (24/09/2026). Restoring the company but not this step left
        // January / 28 days / "included" showing over a real company's answers, and saving
        // the step wrote them back — the same fault as the empty address, one step along.
        setLeavePolicy((p) => ({
          ...p,
          leave_year_start_month: t.leave_year_start_month ?? p.leave_year_start_month,
          default_annual_allowance: t.default_annual_allowance ?? p.default_annual_allowance,
          bank_holidays_included: t.bank_holidays_included ?? p.bank_holidays_included,
        }));
      })
      // A brand-new company has nothing to restore and this may legitimately fail before
      // the first save. The form simply stays empty, which is correct for a real signup.
      .catch(() => {});
    onboardingApi.getStatus().then(r => { if (r.data && r.data.company_name) setCompanyInfo(p => ({ ...p, company_name: p.company_name || r.data.company_name })); }).catch(() => {});
    // The admin's own "days already taken", from THEIR BALANCE — the one place it lives —
    // so going through setup again shows and re-sends what is recorded. A failed load
    // leaves it null, which the server treats as "leave it alone", never as 0.
    leaveApi.getMyAllowances()
      .then((r) => {
        const annual = (r.data || []).find((a) => a.leave_type_name === 'Annual Leave');
        if (annual) setLeavePolicy((p) => ({ ...p, admin_opening_balance_used: annual.opening_used ?? 0 }));
      })
      .catch(() => {});
  }, []);

  // ⚠️ A HINT, NEVER THE ANSWER. This sets what we'd OFFER; only the admin choosing an
  // option below decides anything. Same rule as the Locations editor — if you are ever
  // tempted to apply it here to save a click, read backend/services/working_weeks.py.
  useEffect(() => {
    const code = companyInfo.country_code;
    if (!code) { setWeekSuggestion(null); setWeekChoice(null); return; }
    let cancelled = false;
    locationsApi.getWorkingWeekSuggestion(code)
      .then((res) => { if (!cancelled) setWeekSuggestion(res.data?.suggestion || null); })
      // A hint that fails to load is not worth a toast at signup — the company simply
      // isn't asked, and stays on the app default exactly as it did before this existed.
      .catch(() => { if (!cancelled) setWeekSuggestion(null); });
    return () => { cancelled = true; };
  }, [companyInfo.country_code]);

  // ⚠️ ONLY EVER SETS AN UNANSWERED QUESTION. If the admin has touched the radios on this
  // visit, `weekChoice` is theirs and nothing here may overwrite it.
  useEffect(() => {
    if (weekChoice !== null || !savedWeek.length) return;
    const found = savedChoice();
    if (!found) return;
    setWeekChoice(found);
    if (found === 'custom') setCustomDays(savedWeek);
  }, [savedWeek, weekSuggestion]); // eslint-disable-line

  useEffect(() => {
    if (active.key === 'locations') {
      locationsApi.list().then(r => setExistingLocations(r.data || [])).catch(() => {});
    }
    if (active.key === 'leave') {
      loadHolidays();
      // Asked AFTER the company step has saved, so it answers for the country and week
      // just chosen. A failure shows no line at all — never a UK default (D9).
      settingsApi.getJurisdiction()
        .then((r) => setJurisdiction(r.data || null))
        .catch(() => setJurisdiction(null));
    }
  }, [step]); // eslint-disable-line

  // The bank holidays this company actually has, plus the ones they've turned off.
  //
  // ⚠️ HEAD OFFICE ONLY (Rob, 13/08/2026). `companyBankHolidays` filters to the company's
  // own country and region — the same filter the Settings headline uses, so signup and
  // Settings cannot report different numbers. Extra sites keep their own days.
  const loadHolidays = async () => {
    try {
      const yr = new Date().getFullYear();
      const [all, out, review] = await Promise.all([
        holidaysApi.list(yr),
        holidaysApi.listOptedOut().catch(() => ({ data: [] })),
        holidaysApi.getReview().catch(() => ({ data: null })),
      ]);
      const ours = companyBankHolidays(
        all.data, companyInfo.country_code, companyInfo.public_holiday_region);
      setLoadedHolidayCount(ours.length);
      setLoadedHolidays(ours);
      setOptedOutHolidays(out.data || []);
      // How many OTHER places this company has days for — only used to admit, honestly,
      // that this list is the head office's and not everyone's.
      setOtherPlaceCount(
        otherCountryCodes(all.data, companyInfo.country_code).length);
      setHolidayPlaces(placesIn(all.data));
      if (review.data && review.data.needs_review === false) setHolidaysConfirmed(true);
    } catch (e) { /* a list that won't load is not worth blocking signup over */ }
  };

  const removeHoliday = async (holiday) => {
    if (!window.confirm(removeQuestion(holiday))) return;
    try {
      const { data } = await holidaysApi.delete(holiday.id);
      toast.success(removedMessage(holiday, data?.message));
      loadHolidays();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove that holiday');
    }
  };

  const restoreHoliday = async (holiday) => {
    try {
      await holidaysApi.restore(holiday.id);
      toast.success(restoredMessage(holiday.name));
      loadHolidays();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not restore that holiday');
    }
  };

  // ⚠️ THE SAME SHAPE THE HOLIDAYS PAGE USES, because it is the same form component.
  // An earlier version of this stamped the company's own country on every day — better
  // than the hard-coded 'GB' it replaced, and still an answer nobody was asked for. Rob,
  // 13/08/2026: a local religious day belongs to the place that observes it; a founders'
  // day belongs to everyone, and only the admin knows which they are adding.
  const addCompanyDay = async ({ name, date, country_code, region, problem }) => {
    if (problem) { toast.error(problem); return false; }
    try {
      await holidaysApi.create({ name, date, country_code, region });
      toast.success('Added to your calendar');
      loadHolidays();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add that day');
      return false;
    }
  };

  // ⚠️ THE SAME ENDPOINT THE HOLIDAYS PAGE CALLS. Unticking cannot un-confirm — there is
  // no endpoint for that and inventing one would be a new rule at the worst moment. The
  // box simply reflects the recorded state once it is set.
  const confirmHolidays = async (checked) => {
    if (!checked) return;
    try {
      await holidaysApi.confirmReview();
      setHolidaysConfirmed(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save that');
    }
  };

  const ci = (f, v) => setCompanyInfo(p => ({ ...p, [f]: v }));
  const lp = (f, v) => setLeavePolicy(p => ({ ...p, [f]: v }));
  const updLocation = (idx, patch) => setLocations(bs => bs.map((x, i) => i === idx ? { ...x, ...patch } : x));

  // Save the current step's data (company + leave are persisted immediately; the
  // list steps are created together at Complete to avoid duplicates on back/next).
  const saveCurrentStep = async () => {
    if (active.key === 'company') {
      await onboardingApi.updateCompanyInfo({
        ...companyInfo,
        employee_count: companyInfo.employee_count ? parseInt(companyInfo.employee_count) : null,
        // ⚠️ OMITTED ENTIRELY WHEN UNANSWERED, not sent as null. The company step is saved
        // every time the admin moves between steps, so sending a null here would overwrite
        // a week they had already set — the "not sent vs sent as empty" distinction that
        // has now bitten this project five times.
        ...(chosenWeek() ? { business_hours: chosenWeek() } : {}),
      });
      await onboardingApi.updateAdminInfo(adminInfo).catch(() => {});
      // Holidays are loaded by the server as part of saving this step — it knows the
      // country, and it uses the one entry point every other path uses.
      setLeavePolicy(p => ({ ...p, public_holiday_region: companyInfo.public_holiday_region }));
    } else if (active.key === 'leave') {
      await onboardingApi.updateLeavePolicy(leavePolicy);
    }
  };

  const goToStep = async (i) => {
    if (i === step) return;
    setLoading(true);
    try { await saveCurrentStep(); setStep(i); }
    catch (e) { toast.error('Could not save this step — please check and try again.'); }
    finally { setLoading(false); }
  };

  const handleNext = async () => {
    // ⚠️ REQUIRED, NOT DEFAULTED (Rob, 02/08/2026). It used to arrive pre-set to
    // England and Wales — right for most, and therefore never questioned by the ones
    // it was wrong for. A Scottish or Northern Irish company would sail through signup
    // and quietly get the wrong bank holidays for ever.
    if (step === 0 && !companyInfo.country_code) {
      toast.error('Choose your country — it decides which public holidays your staff get');
      return;
    }
    // Only the countries that HAVE regions may demand one. Asking an Irish or Algerian
    // company for a "UK region" would be nonsense, and blocking them on it worse.
    if (step === 0 && regionsFor(companyInfo.country_code).length > 0 && !companyInfo.public_holiday_region) {
      toast.error('Choose your public holiday region — it decides which bank holidays your staff get');
      return;
    }
    // ⚠️ AN ALL-OFF WEEK NEVER LEAVES THIS SCREEN. The API rejects it, but more to the
    // point the backend's `build_pattern` reads `days = days or [...]` and turns an empty
    // list into Monday to Friday SILENTLY — so the admin who picked no days would end up
    // with the one week they were trying not to have.
    if (step === 0 && weekChoice === 'custom' && !customDays.length) {
      toast.error('Pick at least one working day, or choose one of the options above');
      return;
    }
    if (step < steps.length - 1) { await goToStep(step + 1); }
    else { await handleComplete(); }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await saveCurrentStep().catch(() => {});
      // Locations
      const newLocations = locations.filter(b => (b.name || '').trim());
      for (const b of newLocations) {
        await locationsApi.create({
          name: b.name, address_line_1: b.address_line_1, address_line_2: b.address_line_2,
          address_line_3: b.address_line_3,
          city: b.city, county: b.county, postcode: b.postcode,
          // Extra sites inherit the company's country unless the site says otherwise —
          // a second office is overwhelmingly in the same country, and the admin can
          // change it on the Locations page. The REGION still falls back to the
          // company's, which is only meaningful where regions exist.
          country_code: b.country_code || companyInfo.country_code,
          public_holiday_region: b.region || companyInfo.public_holiday_region,
        }).catch(() => {});
      }
      if (newLocations.length) { await locationsApi.updateSettings({ multi_location_enabled: true }).catch(() => {}); }
      // Departments (build name -> id map for inviting)
      const nameToId = {};
      for (const d of departments.filter(d => (d.name || '').trim())) {
        try { const res = await departmentsApi.create({ name: d.name }); if (res.data && res.data.id) nameToId[d.name] = res.data.id; } catch (e) { /* skip */ }
      }
      // Invited team
      for (const p of invites.filter(p => (p.email || '').trim() && (p.first_name || '').trim())) {
        await usersApi.create({
          first_name: p.first_name, last_name: p.last_name, email: p.email, role: p.role || 'employee',
          department_id: p.department && nameToId[p.department] ? nameToId[p.department] : null,
          must_change_password: true, send_login_email: false,
        }).catch(() => {});
      }
      await onboardingApi.complete();
      toast.success('Setup complete! Welcome to HolidayHive');
      if (refreshUser) await refreshUser();
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (error) {
      toast.error('Failed to complete setup');
    } finally { setLoading(false); }
  };

  const handleSkip = async () => {
    try { await onboardingApi.skip(); } catch (e) { /* ignore */ }
    toast.info('You can finish setup anytime from Settings');
    navigate('/dashboard');
  };

  // ⚠️ NO FALLBACK LABEL. This used to end `|| 'England and Wales'`, so a location with no
  // region set was DISPLAYED as England and Wales — the summary screen confidently
  // reporting an answer nobody gave. If it isn't set, say so.
  const regionLabel = (val) =>
    (options.public_holiday_regions.find(r => r.value === val) || {}).label || 'Not set';

  // ⚠️ "BANK HOLIDAYS: NOT SET — 14 LOADED" — Rob's Algerian signup, 13/08/2026. Nothing
  // was unset: Algeria has no regions to choose between. The honest label there is the
  // COUNTRY, and `lib/regions.js` has always known that; this page had its own function
  // that fell back to the words "Not set" and told a new customer their setup was broken
  // on their first day.
  const holidayScope = () =>
    holidayScopeLabel(countryName(companyInfo.country_code), companyInfo.public_holiday_region);

  return (
    <div className="min-h-screen bg-background">
      <BrandedToaster />

      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Hexagon className="w-7 h-7 text-primary fill-primary/20" />
            <span className="font-heading font-bold text-xl">HolidayHive</span>
          </div>
          <Button variant="ghost" onClick={handleSkip} data-testid="skip-onboarding-btn">Skip for now<X className="w-4 h-4 ml-2" /></Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-5">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ac.chip}`}><active.Icon className="w-5 h-5" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold">Let's set up HolidayHive</h1>
            <p className="text-muted-foreground text-sm">Step {step + 1} of {steps.length} — only the company step is needed; skip anything else and add it later.</p>
          </div>
        </div>

        <div className="h-1.5 rounded-full bg-secondary mb-4 overflow-hidden">
          <div className={`h-full transition-all ${ac.bar}`} style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {steps.map((s, i) => {
            const c = STEP_COLORS[s.color]; const on = i === step;
            return (
              <button key={s.key} type="button" disabled={loading} onClick={() => goToStep(i)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? c.tab + ' font-medium' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${on ? c.bar + ' text-white' : 'bg-secondary'}`}>{i < step ? <Check className="w-3 h-3" /> : i + 1}</span>
                <s.Icon className="w-4 h-4" />{s.label}
              </button>
            );
          })}
        </div>

        <div className="card-hive p-6 md:p-8 min-h-[340px]">

          {active.key === 'company' && (
            <div className="space-y-6">
              <p className="text-sm text-muted-foreground">Tell us about your company. Your address becomes your Head Office, and we'll auto-load the right bank holidays for your region.</p>
              <div>
                <label className="block text-sm font-medium mb-2">Company name</label>
                <Input value={companyInfo.company_name} onChange={(e) => ci('company_name', e.target.value)} placeholder="Your company name" />
              </div>
              <AddressLookup
                initialPostcode={companyInfo.postcode}
                onPostcodeChange={(v) => ci('postcode', v)}
                onAddressSelect={(a) => setCompanyInfo(p => ({ ...p, address_line_1: a.address_line_1 || '', address_line_2: a.address_line_2 || '', city: a.city || '', county: a.county || p.county, postcode: a.postcode || '' }))}
              />
              <div className="grid gap-5 md:grid-cols-2">
                <div className="md:col-span-2"><label className="block text-sm font-medium mb-2">Address line 1</label><Input value={companyInfo.address_line_1} onChange={(e) => ci('address_line_1', e.target.value)} placeholder="123 Business Street" /></div>
                <div className="md:col-span-2"><label className="block text-sm font-medium mb-2">Address line 2</label><Input value={companyInfo.address_line_2} onChange={(e) => ci('address_line_2', e.target.value)} placeholder="Suite 100 (optional)" /></div>
                {/* ⚠️ THE COMPANY ADDRESS BECOMES THE HEAD OFFICE, so a line missing here
                    is a line the head office is born without — the "absent fields" fault
                    Rob named on 08/08/2026, at the one moment the two records are meant to
                    start life identical. */}
                <div className="md:col-span-2"><label className="block text-sm font-medium mb-2">Address line 3</label><Input value={companyInfo.address_line_3} onChange={(e) => ci('address_line_3', e.target.value)} placeholder="Optional" /></div>
                <div><label className="block text-sm font-medium mb-2">City</label><Input value={companyInfo.city} onChange={(e) => ci('city', e.target.value)} placeholder="London" /></div>
                {/* ⚠️ THE SHARED CONTROL, 13/08/2026. This screen had its own hand-rolled
                    version of the same idea, fed by a county list the BACKEND keeps
                    (`routers/onboarding.UK_COUNTIES`) — a fifth copy, in another language.
                    It happens to match the frontend's list exactly today; nothing was
                    keeping it that way, and `tests/test_counties_do_not_drift.py` now
                    does. The company's own county is the head office's county, so it had
                    better be spelled the way the Locations screen spells it. */}
                <CountyField
                  labelClassName="block text-sm font-medium mb-2"
                  countryCode={companyInfo.country_code}
                  value={companyInfo.county}
                  onChange={(county) => ci('county', county)}
                />
                <div><label className="block text-sm font-medium mb-2">Country <span className="text-destructive">*</span></label>
                  <select
                    value={companyInfo.country_code}
                    onChange={(e) => setCompanyInfo(p => ({
                      ...p,
                      country_code: e.target.value,
                      // Region and county belong to a country. Changing it must clear
                      // them, or a company that switches from the UK to Ireland keeps
                      // "scotland" and a Hampshire address.
                      public_holiday_region: e.target.value === p.country_code ? p.public_holiday_region : '',
                      county: e.target.value === p.country_code ? p.county : '',
                    }))}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Choose…</option>
                    {countries.map((c) => (<option key={c.code} value={c.code}>{c.name}</option>))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">Which country's public holidays your staff get.</p>
                  {companyInfo.country_code && companyInfo.country_code !== 'GB' && (
                    <p className="text-xs text-amber-600 mt-1">
                      Public holidays outside the UK come from a community-maintained
                      source. Please check them once you're in.
                    </p>
                  )}
                </div>
                {regionsFor(companyInfo.country_code).length > 0 && (
                <div><label className="block text-sm font-medium mb-2">Public holiday region <span className="text-destructive">*</span></label>
                  <select value={companyInfo.public_holiday_region} onChange={(e) => ci('public_holiday_region', e.target.value)} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    <option value="">Choose…</option>
                    {regionsFor(companyInfo.country_code).map((r) => (<option key={r.code} value={r.code}>{r.name}</option>))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">For your main office. Add other regions in the next step.</p>
                </div>
                )}
                {/* ⚠️ ONLY WHEN THE COUNTRY SAYS IT IS WORTH ASKING (Rob's option A).
                    A UK signup never sees this: Monday to Friday is already right, and a
                    question with an obvious answer only teaches people to click past
                    questions. A company in Algeria or Saudi Arabia is asked HERE, next to
                    the country they just picked, because that is where the reason is. */}
                {suggestedWeekDiffers && (
                <div className="md:col-span-2 rounded-lg border border-blue-500/20 bg-blue-500/5 p-4 space-y-3">
                  <p className="text-sm font-medium">Working week</p>
                  <p className="text-sm text-muted-foreground">
                    Most employers in {countryName(companyInfo.country_code)} work{' '}
                    {describeDays(weekSuggestion.days)}. Is that your working week?
                  </p>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" className="w-4 h-4 mt-0.5"
                      checked={weekChoice === 'suggested'}
                      onChange={() => setWeekChoice('suggested')} />
                    <span className="text-sm">Yes — {describeDays(weekSuggestion.days)}</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" className="w-4 h-4 mt-0.5"
                      checked={weekChoice === 'monday-to-friday'}
                      onChange={() => setWeekChoice('monday-to-friday')} />
                    <span className="text-sm">No — {describeDays(MONDAY_TO_FRIDAY)}</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" className="w-4 h-4 mt-0.5"
                      checked={weekChoice === 'custom'}
                      onChange={() => setWeekChoice('custom')} />
                    <span className="text-sm">Something else — let me pick the days</span>
                  </label>
                  {/* ⚠️ THE DAYS ARE PICKED HERE, NOT LEFT FOR LATER (Rob's option D,
                      13/08/2026). The alternative was to leave them on Monday to Friday
                      and trust them to fix it in Settings — which is a wrong answer they
                      never typed, the exact fault this step exists to remove.

                      ⚠️ DAYS ONLY, NOT HOURS, AND THAT IS DELIBERATE. `WorkingHoursEditor`
                      sets `minWidth: 912px` — seven days, two sessions, times and actions
                      genuinely need it — and this page is `max-w-3xl` (768px), so it would
                      overflow off the side exactly as it did in the Locations dialog on
                      06/08/2026. Start and end times land on the standard 9-5 and are
                      refined in Settings -> Working Hours, which is where hours live. */}
                  {weekChoice === 'custom' && (
                    <div className="pt-1">
                      <div className="flex flex-wrap gap-2">
                        {DAY_KEYS.map((day) => (
                          <button key={day} type="button"
                            onClick={() => setCustomDays(customDays.includes(day)
                              ? customDays.filter((d) => d !== day)
                              : DAY_KEYS.filter((d) => d === day || customDays.includes(d)))}
                            className={`text-xs px-3 py-1.5 rounded-full border capitalize ${
                              customDays.includes(day)
                                ? 'border-blue-500/50 bg-blue-500/15 text-blue-600'
                                : 'border-border text-muted-foreground hover:text-foreground'}`}>
                            {day.slice(0, 3)}
                          </button>
                        ))}
                      </div>
                      {/* ⚠️ AN ALL-OFF WEEK IS REFUSED. The API rejects it anyway, but the
                          admin should be told here rather than at Next. */}
                      <p className={`text-xs mt-2 ${customDays.length ? 'text-muted-foreground' : 'text-destructive'}`}>
                        {customDays.length
                          ? `Your week: ${describeDays(customDays)}. Hours default to 9am-5pm — set them in Settings later.`
                          : 'Pick at least one working day.'}
                      </p>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    This decides which days count as holiday when someone books time off.
                    You can change it any time in Settings.
                  </p>
                </div>
                )}
                <div><label className="block text-sm font-medium mb-2">Company phone</label><Input value={companyInfo.company_phone} onChange={(e) => ci('company_phone', e.target.value)} placeholder="+44 20 1234 5678" /></div>
                <div><label className="block text-sm font-medium mb-2">Website</label><Input value={companyInfo.company_website} onChange={(e) => ci('company_website', e.target.value)} placeholder="https://yourcompany.com" /></div>
                <div><label className="block text-sm font-medium mb-2">Industry</label>
                  <select value={companyInfo.industry} onChange={(e) => ci('industry', e.target.value)} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    <option value="">Select industry...</option>
                    {options.industries.map((i) => (<option key={i} value={i}>{i}</option>))}
                  </select>
                </div>
                <div><label className="block text-sm font-medium mb-2">Number of employees</label><Input type="number" value={companyInfo.employee_count} onChange={(e) => ci('employee_count', e.target.value)} placeholder="e.g. 25" /></div>
              </div>
              <div className="border-t border-border pt-5">
                <p className="text-sm font-medium mb-3">About you</p>
                <div className="grid gap-5 md:grid-cols-2">
                  <div><label className="block text-sm font-medium mb-2">Your job title</label><Input value={adminInfo.job_title} onChange={(e) => setAdminInfo(p => ({ ...p, job_title: e.target.value }))} placeholder="e.g. HR Manager" /></div>
                  <div><label className="block text-sm font-medium mb-2">How did you hear about us?</label>
                    <select value={adminInfo.referral_source} onChange={(e) => setAdminInfo(p => ({ ...p, referral_source: e.target.value }))} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                      <option value="">Select...</option>
                      {options.referral_sources.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {active.key === 'locations' && (
            <div className="space-y-5">
              <p className="text-sm text-muted-foreground">Got more than one site? Add them here — each can follow its own nation's bank holidays. Single-site? Just skip this step.</p>
              {existingLocations.length > 0 && (
                <div className="rounded-lg border border-border bg-secondary/30 p-3 flex items-center gap-3">
                  <Home className="w-5 h-5 text-purple-500" />
                  <div className="text-sm">
                    <p className="font-medium">{existingLocations[0].name || 'Head Office'} <span className="text-muted-foreground font-normal">— created from your company address</span></p>
                    <p className="text-xs text-muted-foreground">{[existingLocations[0].city, regionLabel(existingLocations[0].public_holiday_region)].filter(Boolean).join(' · ')}</p>
                  </div>
                </div>
              )}
              {locations.map((b, idx) => (
                <div key={idx} className="rounded-lg border border-border p-4 space-y-3">
                  <div className="flex items-center justify-between"><span className="text-sm font-medium">Additional site {idx + 1}</span><button type="button" onClick={() => setLocations(locations.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button></div>
                  <div><label className="block text-sm font-medium mb-1">Site name</label><Input value={b.name} onChange={(e) => updLocation(idx, { name: e.target.value })} placeholder="e.g. Edinburgh Office" /></div>
                  <AddressLookup initialPostcode={b.postcode} onPostcodeChange={(v) => updLocation(idx, { postcode: v })} onAddressSelect={(a) => updLocation(idx, { address_line_1: a.address_line_1 || '', address_line_2: a.address_line_2 || '', city: a.city || '', county: a.county || b.county, postcode: a.postcode || '' })} />
                  <div><label className="block text-sm font-medium mb-1">Address line 1</label><Input value={b.address_line_1} onChange={(e) => updLocation(idx, { address_line_1: e.target.value })} placeholder="Street / road name" /></div>
                  <div><label className="block text-sm font-medium mb-1">Address line 2</label><Input value={b.address_line_2} onChange={(e) => updLocation(idx, { address_line_2: e.target.value })} placeholder="Optional" /></div>
                  {/* Added 13/08/2026 — every other address form in the app has three
                      lines, and a site typed here used to lose the third one. */}
                  <div><label className="block text-sm font-medium mb-1">Address line 3</label><Input value={b.address_line_3} onChange={(e) => updLocation(idx, { address_line_3: e.target.value })} placeholder="Optional" /></div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div><label className="block text-sm font-medium mb-1">City / town</label><Input value={b.city} onChange={(e) => updLocation(idx, { city: e.target.value })} placeholder="City" /></div>
                    {/* ⚠️ THE SHARED CONTROL. This was a plain typing box — the FOURTH
                        county input in the app and the first one a customer ever meets,
                        so the county most likely to be spelled unlike everywhere else was
                        the one entered at signup. Extra sites inherit the company's
                        country, so that is the list it offers. */}
                    <CountyField
                      id={`county-${idx}`}
                      labelClassName="block text-sm font-medium mb-1"
                      countryCode={b.country_code || companyInfo.country_code}
                      value={b.county}
                      onChange={(county) => updLocation(idx, { county })}
                    />
                    {/* ⚠️ WAS `b.region || 'england-and-wales'` WITH NO EMPTY OPTION, so an
                        Edinburgh office added at signup silently defaulted to English bank
                        holidays and the admin had no way to see it had been decided for
                        them. Shown only where regions exist; a site in a country without
                        them isn't asked. Extra sites inherit the company's COUNTRY — set a
                        different one afterwards on the Locations page. */}
                    {regionsFor(companyInfo.country_code).length > 0 && (
                    <div><label className="block text-sm font-medium mb-1">Public holiday region</label>
                      <select value={b.region || ''} onChange={(e) => updLocation(idx, { region: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                        <option value="">Choose…</option>
                        {regionsFor(companyInfo.country_code).map((r) => (<option key={r.code} value={r.code}>{r.name}</option>))}
                      </select>
                    </div>
                    )}
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" className="w-full" onClick={() => setLocations([...locations, { name: '', address_line_1: '', address_line_2: '', address_line_3: '', city: '', county: '', postcode: '', region: companyInfo.public_holiday_region }])}><Plus className="w-4 h-4 mr-2" />Add another site</Button>
            </div>
          )}

          {active.key === 'departments' && (
            <div className="space-y-5">
              <p className="text-sm text-muted-foreground">Set up your teams or departments. You can assign managers and people to them later. Skip if you'd rather do this afterwards.</p>
              <div className="flex flex-wrap gap-2">
                {DEPT_SUGGESTIONS.filter(s => !departments.some(d => d.name === s)).map((s) => (
                  <button key={s} type="button" onClick={() => setDepartments([...departments, { name: s }])} className="text-xs px-3 py-1 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-amber-500/50"><Plus className="w-3 h-3 inline mr-1" />{s}</button>
                ))}
              </div>
              <div className="space-y-2">
                {departments.map((d, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input value={d.name} onChange={(e) => setDepartments(departments.map((x, i) => i === idx ? { name: e.target.value } : x))} placeholder="Department name" />
                    <button type="button" onClick={() => setDepartments(departments.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-destructive p-2"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" className="w-full" onClick={() => setDepartments([...departments, { name: '' }])}><Plus className="w-4 h-4 mr-2" />Add department</Button>
            </div>
          )}

          {active.key === 'leave' && (
            <div className="space-y-6">
              <p className="text-sm text-muted-foreground">Set the default leave policy for your company. You can fine-tune allowances per person later.</p>
              <div className="grid gap-5 md:grid-cols-2">
                <div><label className="block text-sm font-medium mb-2">Leave year starts</label>
                  <select value={leavePolicy.leave_year_start_month} onChange={(e) => lp('leave_year_start_month', parseInt(e.target.value))} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    {months.map((m, i) => (<option key={m} value={i + 1}>{m}</option>))}
                  </select>
                </div>
                <div><label className="block text-sm font-medium mb-2">Default annual allowance (days)</label><Input type="number" value={leavePolicy.default_annual_allowance} onChange={(e) => lp('default_annual_allowance', parseFloat(e.target.value))} /></div>
                <div className="md:col-span-2"><label className="block text-sm font-medium mb-2">Days you've already taken this leave year</label><Input type="number" step="0.5" min="0" placeholder="0" value={leavePolicy.admin_opening_balance_used ?? ''} onChange={(e) => lp('admin_opening_balance_used', e.target.value === '' ? null : (parseFloat(e.target.value) || 0))} /><p className="text-xs text-muted-foreground mt-1">Signing up mid-year? Enter holiday you took before you started using HolidayHive. Anything booked in HolidayHive is counted automatically — don't include it here.</p></div>
                <div className="md:col-span-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4 space-y-3">
                  <div className="flex items-center gap-2"><Calendar className="w-5 h-5 text-emerald-600" /><p className="text-sm font-medium">Bank holidays: {holidayScope()}{loadedHolidayCount > 0 ? ` — ${loadedHolidayCount} loaded` : ''}</p></div>
                  <p className="text-sm font-medium">How does your {leavePolicy.default_annual_allowance || 0}-day allowance work with bank holidays?</p>
                  <label className="flex items-start gap-2 cursor-pointer"><input type="radio" checked={leavePolicy.bank_holidays_included === true} onChange={() => lp('bank_holidays_included', true)} className="w-4 h-4 mt-0.5" /><span className="text-sm"><strong>Included</strong> — bank holidays come out of the allowance ({Math.max(0, (leavePolicy.default_annual_allowance || 0) - loadedHolidayCount)} days + {loadedHolidayCount} bank holidays = <strong>{leavePolicy.default_annual_allowance || 0} days</strong>).</span></label>
                  <label className="flex items-start gap-2 cursor-pointer"><input type="radio" checked={leavePolicy.bank_holidays_included === false} onChange={() => lp('bank_holidays_included', false)} className="w-4 h-4 mt-0.5" /><span className="text-sm"><strong>On top</strong> — staff get {leavePolicy.default_annual_allowance || 0} days <em>plus</em> {loadedHolidayCount} bank holidays = <strong>{(leavePolicy.default_annual_allowance || 0) + loadedHolidayCount} days</strong>.</span></label>
                  <div className="pt-2 border-t border-emerald-500/20"><p className="text-sm">Total time off per year: <strong className="text-emerald-600 text-base">{leavePolicy.bank_holidays_included ? (leavePolicy.default_annual_allowance || 0) : ((leavePolicy.default_annual_allowance || 0) + loadedHolidayCount)} days</strong></p>{(() => {
                    // ⚠️ ADVICE, NEVER A BLOCK (D4): amber words, and Next still works.
                    // Nothing at all for a country we hold no law for (D9) — this line
                    // used to tell every company, French or Algerian, about the UK's 28.
                    const line = statutoryLine({
                      jurisdiction,
                      allowance: leavePolicy.default_annual_allowance,
                      bankHolidays: loadedHolidayCount,
                      included: leavePolicy.bank_holidays_included,
                    });
                    if (!line) return null;
                    const counts = line.bankHolidaysCount ? ', and bank holidays can count towards it' : '';
                    const source = line.sourceUrl && (<> <a href={line.sourceUrl} target="_blank" rel="noreferrer" className="underline">source</a></>);
                    return (<>
                      {line.state === 'meets' ? (
                        <p className="text-xs text-emerald-700 mt-2" data-testid="statutory-line">✓ Meets the {line.name} minimum — {line.working}{counts}.{source}</p>
                      ) : (
                        <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5" data-testid="statutory-line">
                          <p className="text-sm font-medium text-amber-700">{line.shortBy} below the {line.name} minimum</p>
                          <p className="text-xs text-muted-foreground mt-0.5">For your week the minimum is {line.working}{counts}. This is advice — you can still carry on, and change it later in Settings → Leave policy.{source}</p>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">Part-timers get their own minimum, worked out from their own week — you'll see it on each person when you add them.</p>
                    </>);
                  })()}</div>
                  {/* ⚠️ STEP 5'S REVIEW, AT THE ONE MOMENT SOMEBODY IS ALREADY LOOKING.
                      Without this a customer who checked their dates during signup would
                      still be emailed in January asking them to check their dates — and
                      the Perfection Box would carry an amber item about a job they had
                      already done. Ticking it records the CALENDAR year (decision L). */}
                  {loadedHolidayCount > 0 && (
                  <div className="pt-2 border-t border-emerald-500/20 space-y-2">
                    <button type="button" onClick={() => setShowHolidayDates(!showHolidayDates)}
                      className="text-sm text-emerald-700 hover:underline">
                      {showHolidayDates ? 'Hide your bank holidays' : 'Check your bank holidays'}
                    </button>
                    {showHolidayDates && (
                      <div className="rounded-lg border border-emerald-500/20 bg-background p-3">
                        {/* ⚠️ THE SAME COMPONENT THE HOLIDAYS PAGE RENDERS. Rob, 13/08/2026:
                            *"We know where to add or check them as we built it, but the new
                            customer does not."* Right — a read-only list here assumed they
                            would find the Holidays page on their own. */}
                        <HolidayDayList
                          dense
                          holidays={loadedHolidays}
                          optedOut={optedOutHolidays}
                          canEdit
                          onRemove={removeHoliday}
                          onRestore={restoreHoliday}
                          countryNames={countryNamesByCode()}
                          multiCountry={otherPlaceCount > 0}
                        />
                        {/* ⚠️ HEAD OFFICE ONLY AT SIGNUP (Rob's call, 13/08/2026). A company
                            that added an Edinburgh site two steps ago already has two sets
                            of dates, and signup is the worst moment to introduce per-office
                            holidays. The extra sites keep their own days untouched, and the
                            Holidays page is where that conversation belongs. */}
                        {otherPlaceCount > 0 && (
                          <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                            These are {holidayScope()} — your head office. Your other{' '}
                            {otherPlaceCount === 1 ? 'site keeps its own days' : `${otherPlaceCount} sites keep their own days`},
                            which you can check on the Holidays page once you're in.
                          </p>
                        )}
                        <div className="mt-3 pt-3 border-t border-border space-y-2">
                          <p className="text-sm font-medium">Add a day your company takes off</p>
                          {/* ⚠️ THE SAME FORM THE HOLIDAYS PAGE RENDERS. The place picker
                              only appears once a company has more than one place, so a
                              single-office signup is not asked a question with one
                              possible answer. */}
                          <AddCompanyDayForm
                            compact
                            places={holidayPlaces}
                            countryNames={countryNamesByCode()}
                            onAdd={addCompanyDay}
                          />
                        </div>
                      </div>
                    )}
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 mt-0.5"
                        checked={holidaysConfirmed}
                        onChange={(e) => confirmHolidays(e.target.checked)} />
                      <span className="text-sm">These look right for {new Date().getFullYear()}</span>
                    </label>
                    {/* ⚠️ NOT REQUIRED. Blocking signup on a tick box only teaches people to
                        tick it — the January email is the backstop, and a better one. */}
                    <p className="text-xs text-muted-foreground">
                      Optional — we'll remind you each January if you'd rather check later.
                      You can change any of this later on the Holidays page.
                    </p>
                  </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {active.key === 'team' && (
            <div className="space-y-5">
              <p className="text-sm text-muted-foreground">Add your team now or later. Accounts are created with "set password on first login" — you can send invites from the Employees page when you're ready.</p>
              {invites.map((p, idx) => (
                <div key={idx} className="rounded-lg border border-border p-3 space-y-3">
                  <div className="flex items-center justify-between"><span className="text-sm font-medium">Person {idx + 1}</span><button type="button" onClick={() => setInvites(invites.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button></div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <Input value={p.first_name} onChange={(e) => setInvites(invites.map((x, i) => i === idx ? { ...x, first_name: e.target.value } : x))} placeholder="First name" />
                    <Input value={p.last_name} onChange={(e) => setInvites(invites.map((x, i) => i === idx ? { ...x, last_name: e.target.value } : x))} placeholder="Last name" />
                    <Input value={p.email} onChange={(e) => setInvites(invites.map((x, i) => i === idx ? { ...x, email: e.target.value } : x))} placeholder="Email" />
                    <select value={p.role || 'employee'} onChange={(e) => setInvites(invites.map((x, i) => i === idx ? { ...x, role: e.target.value } : x))} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                      <option value="employee">Employee</option><option value="manager">Manager</option><option value="admin">Admin</option>
                    </select>
                    {departments.filter(d => (d.name || '').trim()).length > 0 && (
                      <select value={p.department || ''} onChange={(e) => setInvites(invites.map((x, i) => i === idx ? { ...x, department: e.target.value } : x))} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm md:col-span-2">
                        <option value="">No department</option>
                        {departments.filter(d => (d.name || '').trim()).map((d) => (<option key={d.name} value={d.name}>{d.name}</option>))}
                      </select>
                    )}
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" className="w-full" onClick={() => setInvites([...invites, { first_name: '', last_name: '', email: '', role: 'employee', department: '' }])}><Plus className="w-4 h-4 mr-2" />Add a person</Button>
            </div>
          )}

        </div>

        <div className="flex items-center justify-between mt-6">
          <Button variant="outline" onClick={() => goToStep(Math.max(0, step - 1))} disabled={step === 0 || loading}><ChevronLeft className="w-4 h-4 mr-2" />Back</Button>
          <button type="button" onClick={handleNext} disabled={loading} className={`px-5 py-2 rounded-md text-white text-sm font-medium flex items-center ${ac.btn} disabled:opacity-60`}>
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {step === steps.length - 1 ? (<>Finish setup<Check className="w-4 h-4 ml-2" /></>) : (<>Next<ChevronRight className="w-4 h-4 ml-2" /></>)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
